import { Field, useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import { AddressFieldProps } from '../../types/fields';
import { AddressForm, config, Country } from '../../utils/config';
import getValidator from '../../utils/getValidator';
import { getStreetHouseNumberFormat } from '../../utils/address';
import TextField from '../formik/TextField';
import { useEffect, useRef } from 'react';
import { getAutocompleteAddressFromPlace } from '../../utils/googleMaps';
import { showNotification } from '../molecules/Notifications';

type StreetHouseNumberFieldProps = AddressFieldProps &
  AddressForm['streetHouseNumber'] & { country: Country; isGoogleMapsSdkLoaded: boolean };

const StreetHouseNumberField = (props: StreetHouseNumberFieldProps) => {
  const { context, testIdPrefix, country, separateFields, houseNumber, street, isGoogleMapsSdkLoaded, ...validation } =
    props;
  const { formatMessage } = useIntl();
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();
  const inputRef = useRef<HTMLInputElement>(null);
  const isStateFieldEnabled = !!config.addressForms[country]?.state?.enabled;
  const isStateSelectField = isStateFieldEnabled && config.addressForms[country].state.select.enabled;

  const handleChange = (name: string, value?: string) => {
    setFieldValue(name, value);
    setTimeout(() => setFieldTouched(name, true), 1);
  };
  useEffect(() => {
    if (!isGoogleMapsSdkLoaded || !config.addressForms[country].googleMapsAutocomplete?.enabled) {
      return;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(inputRef.current!, {
        componentRestrictions: { country: [country] },
        fields: ['address_components'],
        types: ['address']
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const searchParams = getAutocompleteAddressFromPlace(place, country, isStateSelectField);
        if (!searchParams) {
          showNotification({
            level: 'warning',
            text: formatMessage({ id: 'shipping.notifications.incompleteAddressComponents' }),
            autoClose: true
          });
          return;
        }
        const { streetHouseNumber, zipCode, city, state, houseNumber: _houseNumber, street: _street } = searchParams;
        handleChange(`${context}.houseNumber`, _houseNumber);
        handleChange(`${context}.zipCode`, zipCode);
        handleChange(`${context}.streetHouseNumber`, streetHouseNumber);
        handleChange(`${context}.street`, _street);
        handleChange(`${context}.city`, city);
        if (isStateFieldEnabled) {
          const stateValue = isStateSelectField ? country + '-' + state : state;
          handleChange(`${context}.state`, stateValue);
        }
      });
    } catch (e: any) {
      console.error(e);
    }
  }, [isGoogleMapsSdkLoaded, country]);

  const houseNumberField = (
    <div className="form__field-container">
      <Field
        key={`${context}.houseNumber`}
        name={`${context}.houseNumber`}
        component={TextField}
        label={formatMessage({ id: 'address.houseNumber' })}
        maxLength={houseNumber.maxLength}
        validate={getValidator(houseNumber)}
        data-test-id={`${testIdPrefix}-houseNumber`}
      />
    </div>
  );

  if (separateFields) {
    return (
      <>
        {houseNumber.position === 'first' && houseNumberField}
        <div className="form__field-container" style={{ flex: 4 }}>
          <Field
            key={`${context}.street`}
            name={`${context}.street`}
            component={TextField}
            label={formatMessage({ id: 'address.street' })}
            inputref={inputRef}
            maxLength={street?.maxLength}
            validate={getValidator(street)}
            data-test-id={`${testIdPrefix}-street`}
          />
        </div>
        {houseNumber.position === 'second' && houseNumberField}
      </>
    );
  } else {
    return (
      <div className="form__field-container">
        <Field
          key={`${context}.streetHouseNumber`}
          name={`${context}.streetHouseNumber`}
          component={TextField}
          inputref={inputRef}
          label={formatMessage({ id: 'address.streetHouseNumber' })}
          maxLength={validation.maxLength}
          validate={getValidator({
            ...validation,
            format: getStreetHouseNumberFormat(country)
          })}
          data-test-id={`${testIdPrefix}-streetHouseNumber`}
        />
      </div>
    );
  }
};

export default StreetHouseNumberField;
